// src/features/products/components/ProductSearch/ProductSearch.tsx
import React from 'react';
import { ProductTable } from './ProductTable';
import { ProductCard } from './ProductCard';
import { TablePropertiesIcon, GalleryHorizontalIcon } from 'lucide-react';
import {
    Alert,
    QueryErrorAlert,
    Button,
    Tooltip,
    Carousel,
    SearchBar,
    LoadingSpinner,
} from '@shared/components';
import { toast } from 'react-toastify';
import { useProductSearchWithQuery } from '../../hooks/useProductSearchWithQuery';
import { RejectionReasonList } from './RejectionReasonList';
import type { Product } from '../../types/products.types';

interface ProductSearchProps {
    onSelectProduct: (product: Product) => void;
    selectedProduct?: Product | null;
    onProductsChange?: (count: number) => void;
}

export const ProductSearch: React.FC<ProductSearchProps> = ({
    onSelectProduct,
    onProductsChange,
    selectedProduct,
}) => {
    const {
        products,
        rejectionReasons,
        searchTerm,
        debouncedSearchTerm,
        setSearchTerm,
        resetSearch,
        refetch,
        view,
        toggleView,
        isLoading,
        isFetching,
        error,
        totalProducts,
        selectedVehicle,
        vehicleParams,
    } = useProductSearchWithQuery();

    const resultsRegionRef = React.useRef<HTMLDivElement>(null);

    const handleRetry = () => {
        resultsRegionRef.current?.focus();
        refetch();
    };

    const handleProductSelect = (product: Product) => {
        onSelectProduct(product);
    };

    // Helper to get vehicle display name
    const getVehicleDisplayName = () => {
        if (!selectedVehicle) return null;

        if ('derivative' in selectedVehicle && selectedVehicle.derivative) {
            const derivative = selectedVehicle.derivative;
            return `${derivative.first_year_of_manufacture} ${derivative.make?.name ?? ''} ${derivative.model?.name ?? ''}`.trim();
        } else if ('make' in selectedVehicle && 'model' in selectedVehicle) {
            return `${selectedVehicle.first_year_of_manufacture} ${selectedVehicle.make.name} ${selectedVehicle.model.name}`;
        }

        return 'Selected Vehicle';
    };

    // Show if search is pending (user is typing but API hasn't been called yet)
    const isSearchPending = searchTerm !== debouncedSearchTerm && searchTerm.length > 0;

    React.useEffect(() => {
        onProductsChange?.(isLoading || error ? 0 : products.length);
    }, [products.length, isLoading, error, onProductsChange]);

    // Surface load failures explicitly (the inline error block was removed): a
    // 500/network error must not be indistinguishable from a valid empty result.
    // toastId dedupes so a re-render does not stack duplicate toasts.
    React.useEffect(() => {
        if (error) {
            toast.error('There was a problem loading products. Please try again.', {
                toastId: 'products-load-error',
            });
        }
    }, [error]);

    return (
        <div className="mt-4 space-y-4">
            <div className="flex flex-wrap justify-between content-center">
                <div className="relative">
                    <SearchBar
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        placeholder={
                            selectedVehicle
                                ? 'Search products for this vehicle'
                                : 'Search for a Product'
                        }
                    />
                    {/* Show search state indicator */}
                    {isSearchPending && (
                        <LoadingSpinner
                            size="sm"
                            decorative
                            className="absolute right-3 top-1/2 transform -translate-y-1/2"
                        />
                    )}
                    {isFetching && !isSearchPending && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <div className="animate-pulse rounded-full h-4 w-4 bg-mp-primary"></div>
                        </div>
                    )}
                </div>
                <div className="flex items-center justify-start md:justify-end">
                    <Tooltip content="Show Product Carousel">
                        <Button
                            onClick={toggleView}
                            className="rounded-l-md"
                            variant={`${view === 'carousel' ? 'primary' : 'outline'}`}
                            rounded={false}
                            size="xs"
                            startIcon={<GalleryHorizontalIcon size={18} />}
                        />
                    </Tooltip>
                    <Tooltip content="Show Product Table">
                        <Button
                            onClick={toggleView}
                            className="rounded-r-md"
                            variant={`${view === 'table' ? 'primary' : 'outline'}`}
                            rounded={false}
                            size="xs"
                            startIcon={<TablePropertiesIcon size={18} />}
                        />
                    </Tooltip>
                </div>
            </div>

            {/* Vehicle Context Display */}
            {selectedVehicle && (
                <Alert variant="info" role="note">
                    <p className="font-medium">Showing products for: {getVehicleDisplayName()}</p>
                </Alert>
            )}

            {/* Results Count with Search State */}
            {products.length > 0 && (
                <div className="text-sm text-mp-muted flex items-center gap-2">
                    <span>
                        {selectedVehicle
                            ? `Showing ${totalProducts} compatible products${debouncedSearchTerm ? ` matching "${debouncedSearchTerm}"` : ''}`
                            : `Showing ${totalProducts} products${debouncedSearchTerm ? ` matching "${debouncedSearchTerm}"` : ''}`}
                    </span>
                    {isSearchPending && <span className="text-mp-muted text-xs">(typing...)</span>}
                </div>
            )}

            {/* Product Display */}
            <div
                ref={resultsRegionRef}
                role="region"
                aria-label="Product results"
                tabIndex={-1}
                className="w-full focus:outline-none"
            >
                {!isLoading && products.length > 0 ? (
                    <>
                        {view === 'carousel' && (
                            <Carousel
                                items={products}
                                renderItem={(product: Product) => (
                                    <ProductCard
                                        product={product}
                                        onSelectProduct={handleProductSelect}
                                        isSelected={selectedProduct?.id === product.id}
                                    />
                                )}
                                keyExtractor={(product: Product) => product.id}
                            />
                        )}
                        {view === 'table' && (
                            <ProductTable data={products} onSelectProduct={handleProductSelect} />
                        )}
                    </>
                ) : isLoading ? (
                    <LoadingSpinner message="Loading authorised products" className="p-4" />
                ) : error ? (
                    <QueryErrorAlert
                        message="There was a problem loading products."
                        onRetry={handleRetry}
                        isRetrying={isFetching}
                    />
                ) : (
                    <Alert variant="warning" role="status">
                        <div className="flex flex-col items-start gap-2">
                            <p className="font-semibold">No Products Found</p>
                            {/* Eligibility reasons are NOT scoped to the search term (the backend
                                candidate finder matches on vehicle identity only), so when a search is
                                active prefer the search-specific message rather than masking it. */}
                            {rejectionReasons.length > 0 && !debouncedSearchTerm ? (
                                <RejectionReasonList reasons={rejectionReasons} />
                            ) : (
                                <p>
                                    {selectedVehicle
                                        ? debouncedSearchTerm
                                            ? `No products match "${debouncedSearchTerm}" for this vehicle`
                                            : 'No compatible products found for this vehicle'
                                        : debouncedSearchTerm
                                          ? `No products match "${debouncedSearchTerm}"`
                                          : 'No products available at the moment'}
                                </p>
                            )}
                            {debouncedSearchTerm && (
                                <button onClick={resetSearch} className="underline">
                                    Clear search{' '}
                                    {selectedVehicle
                                        ? 'and show all compatible products'
                                        : 'and show all products'}
                                </button>
                            )}
                        </div>
                    </Alert>
                )}
            </div>

            {/* Debug Info (remove in production) */}
            {process.env.NODE_ENV === 'development' && (
                <details className="text-xs text-mp-muted border border-mp-border rounded p-2">
                    <summary>Debug: API Parameters & Search State</summary>
                    <pre className="mt-2 whitespace-pre-wrap">
                        {JSON.stringify(
                            {
                                vehicleParams,
                                searchTerm,
                                debouncedSearchTerm,
                                isSearchPending,
                                isFetching,
                            },
                            null,
                            2
                        )}
                    </pre>
                </details>
            )}
        </div>
    );
};
